import React, { useState, useEffect } from 'react';
import { Bookmark } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { toggleBookmark, getBookmarkedIds } from '../services/gig.service';

/**
 * BookmarkButton — shows a bookmark icon on a gig card or details page.
 *  - Hidden for guests (no accessToken).
 *  - Shows filled/outlined based on whether the gig is already bookmarked.
 *  - Calls toggleBookmark on click.
 */
const BookmarkButton = ({ gigId, className = '' }) => {
  const { accessToken } = useAuthStore();
  const [bookmarked, setBookmarked] = useState(false);
  const [loading, setLoading] = useState(false);

  // On mount, check if this gig is already bookmarked by the current user
  useEffect(() => {
    if (!accessToken || !gigId) return;

    getBookmarkedIds()
      .then(({ savedGigs }) => {
        setBookmarked(savedGigs.some((id) => id === gigId || id === gigId?.toString()));
      })
      .catch(() => {}); // Silently fail — user just won't see their saved state
  }, [gigId, accessToken]);

  if (!accessToken) return null; // Only show for signed-in users

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;

    setLoading(true);
    try {
      const { bookmarked: newState } = await toggleBookmark(gigId);
      setBookmarked(newState);
    } catch (err) {
      console.error('Bookmark toggle failed:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      title={bookmarked ? 'Remove bookmark' : 'Save this gig'}
      className={`flex items-center justify-center transition-all duration-200 ${loading ? 'opacity-50' : 'hover:scale-110'} ${className}`}
    >
      <Bookmark
        size={20}
        className={bookmarked ? 'text-[#174568] fill-[#174568]' : 'text-gray-400 hover:text-[#174568]'}
      />
    </button>
  );
};

export default BookmarkButton;
